import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

import { useAppStore } from '@/stores/app';
import { useNotificationStore } from '@/stores/notification';
import { i18n } from '@/plugins/i18n';
import { loadIfOnline } from '@/helpers/lazyLoad';

const Auth = () => loadIfOnline(() => import('@/layouts/auth.vue'));
const Default = () => loadIfOnline(() => import('@/layouts/default.vue'));
const IndexPage = () => loadIfOnline(() => import('@/pages/index.vue'));
const ProfileSelection = () =>
  loadIfOnline(() => import('@/pages/ProfileSelection.vue'));
const PageNotFound = () =>
  loadIfOnline(() => import('@/pages/PageNotFound.vue'));
const LiteRender = () =>
  loadIfOnline(() => import('@/pages/LiteRender/liteRender.vue'));
const LoginView = () => loadIfOnline(() => import('@/pages/LoginView.vue'));
const NoAccess = () => loadIfOnline(() => import('@/pages/NoAccess.vue'));
const AuthCallbackView = () => loadIfOnline(() => import('@/pages/AuthCallbackView.vue'));

const validateBusinessProfileAccess = async (to: RouteLocationNormalized) => {
  const bpId = Number(
    (to.params as Record<string, string>).businessProfileId
  );
  const appStore = useAppStore();
  const notificationStore = useNotificationStore();

  if (Number.isNaN(bpId) || bpId <= 0) {
    notificationStore.notify(
      (i18n.global.t as (key: string) => string)(
        'messages.invalidBusinessProfile'
      ),
      'error'
    );

    return { path: '/PageNotFound' };
  }

  try {
    if (appStore.businessProfiles.length === 0) {
      await appStore.fetchBusinessProfiles();
    }
  } catch (error) {
    const { isForbiddenPortalError } = await import('@/api/errorHandler');
    if (isForbiddenPortalError(error)) {
      return { path: '/no-access' };
    }
    return { path: '/PageNotFound' };
  }

  const hasAccess = appStore.businessProfiles.some(bp => bp.id === bpId);
  if (!hasAccess) {
    return { path: '/PageNotFound' };
  }

  const currentBpId = appStore.getBusinessProfileId;
  if (currentBpId !== bpId) {
    appStore.setSelectedBusinessProfileId(bpId);
  }
};

const routes: RouteRecordRaw[] = [
  {
    path: '/literender',
    name: 'literender',
    component: LiteRender,
    meta: { Headless: true }
  },
  {
    name: 'login',
    path: '/login',
    component: LoginView,
    meta: { title: 'Login' }
  },
  {
    name: 'callback',
    path: '/callback',
    component: AuthCallbackView,
    meta: { title: 'Authenticating...' }
  },
  {
    path: '/profileSelection',
    component: Auth,
    children: [
      {
        path: '',
        name: 'ProfileSelection',
        component: ProfileSelection
      }
    ]
  },
  {
    path: '/',
    component: Default,
    children: [
      {
        path: 'app/:businessProfileId',
        name: 'Editor',
        component: IndexPage,
        beforeEnter: validateBusinessProfileAccess
      },
      {
        path: 'app/:businessProfileId/template/:templateId',
        component: IndexPage,
        beforeEnter: validateBusinessProfileAccess
      }
    ]
  },
  { path: '/:pathMatch(.*)*', name: 'PageNotFound', component: PageNotFound },
  { path: '/no-access', name: 'NoAccess', component: NoAccess }
];

export default routes;
